import {
  normalizeMCPUrl,
  type CachedMCPServer,
  type MCPCatalogEntry,
} from '@littlebird/mcp';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { backendApis } from '@/backendApis';

export const mcpKeys = {
  all: ['mcp'] as const,
  servers: () => [...mcpKeys.all, 'servers'] as const,
  serverStatus: (id: string) => [...mcpKeys.all, 'status', id] as const,
  catalog: () => [...mcpKeys.all, 'catalog'] as const,
};

// Bumped per fetch so a slow background probe from an earlier fetch can't merge
// its stale result over newer state. Module-level: the servers query is global.
let serversFetchGeneration = 0;

/**
 * Present each server with its catalog name when the URL matches a catalog entry.
 * This is a read-only overlay: it self-heals already-connected servers without a
 * migration and never mutates the cache, so it can't fight the optimistic writes
 * in the mutations below.
 */
export function overlayCatalogNames(
  servers: CachedMCPServer[],
  catalog: MCPCatalogEntry[] | undefined
): CachedMCPServer[] {
  if (!catalog?.length) return servers;
  return servers.map((server) => {
    const match = catalog.find(
      (entry) => normalizeMCPUrl(entry.url) === normalizeMCPUrl(server.url)
    );
    return match ? { ...server, name: match.name } : server;
  });
}

export const mcpServersQueryOptions = (queryClient: QueryClient) =>
  queryOptions({
    queryKey: mcpKeys.servers(),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    queryFn: async (): Promise<CachedMCPServer[]> => {
      const generation = ++serversFetchGeneration;
      const servers = await backendApis.mcp.listServers();
      // Probe in background, merge status updates into current cache
      // (don't overwrite — optimistic updates from toggle/delete may have changed the cache)
      backendApis.mcp
        .listServers(true)
        .then((probed) => {
          // A newer fetch superseded us — its result is authoritative; ours is
          // stale and would clobber it.
          if (generation !== serversFetchGeneration) return;
          const probedById = new Map(probed.map((s) => [s.id, s]));
          queryClient.setQueryData<CachedMCPServer[]>(
            mcpKeys.servers(),
            (current) => {
              if (!current) return probed;
              return current.map((s) => {
                const p = probedById.get(s.id);
                if (!p) return s; // server was deleted optimistically — keep it gone
                return { ...p, enabled: s.enabled }; // preserve local enabled state
              });
            }
          );
        })
        .catch(() => {});
      return servers;
    },
  });

export function useMCPServersQuery(enabled = true) {
  const queryClient = useQueryClient();
  const { data: catalog } = useMCPCatalogQuery(enabled);

  const query = useQuery({
    ...mcpServersQueryOptions(queryClient),
    enabled,
  });

  const data = useMemo(
    () => (query.data ? overlayCatalogNames(query.data, catalog) : query.data),
    [query.data, catalog]
  );

  return { ...query, data };
}

export const mcpCatalogQueryOptions = () =>
  queryOptions({
    queryKey: mcpKeys.catalog(),
    queryFn: () => backendApis.mcp.getCatalog(),
    staleTime: 1000 * 60 * 60, // catalog rarely changes
  });

export function useMCPCatalogQuery(enabled = true) {
  return useQuery({
    ...mcpCatalogQueryOptions(),
    enabled,
  });
}

export function useDeleteMCPServerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serverId: string) => backendApis.mcp.deleteServer(serverId),
    onMutate: async (serverId) => {
      await queryClient.cancelQueries({ queryKey: mcpKeys.servers() });
      const previous = queryClient.getQueryData<CachedMCPServer[]>(
        mcpKeys.servers()
      );
      queryClient.setQueryData<CachedMCPServer[]>(mcpKeys.servers(), (old) =>
        old?.filter((s) => s.id !== serverId)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(mcpKeys.servers(), context.previous);
      }
    },
  });
}
